/**
 * Shared app presence helpers ("who is working on / viewing which row").
 *
 * Ephemeral, PHI-free `Basic` resources — one per browser tab — upserted while a
 * staff member has a modal/drawer/page open on a row. `Basic` is used because no
 * patient/guarantor portal policy grants it. See `PRESENCE_*` in `@unify/fhir-model/constants`.
 */

import type { Basic, Practitioner } from '@medplum/fhirtypes';
import {
  PRESENCE_CODE,
  PRESENCE_CODE_SYSTEM,
  PRESENCE_DATA_EXT,
  PRESENCE_SESSION_IDENTIFIER_SYSTEM,
} from '@unify/fhir-model/constants';

/** Screen namespace — prevents row id collisions across tracker vs billing tables. */
export type PresenceScope = 'tracker' | 'billing-claims' | 'billing-encounters';

export type PresenceSubjectResourceType = 'ServiceRequest' | 'Claim' | 'Encounter';

/** One staff member shown as a bubble on a row (deduped across their tabs). */
export interface PresenceActor {
  actorId: string;
  name: string;
  initials: string;
  photoUrl?: string;
}

/** A single parsed presence session (one browser tab). */
export interface PresenceSession {
  actorId: string;
  sessionId: string;
  scope: PresenceScope;
  rowId: string;
  subjectResourceType: PresenceSubjectResourceType;
  name: string;
  initials: string;
  photoUrl?: string;
  expiresAtMs: number;
  active: boolean;
}

export type PresenceTarget = {
  rowId: string;
  scope: PresenceScope;
  subjectResourceType: PresenceSubjectResourceType;
};

interface PresenceDataPayload {
  scope?: PresenceScope;
  rowId: string;
  name: string;
  initials: string;
  photoUrl?: string;
  expiresAt: string;
  active: boolean;
}

const KNOWN_SCOPES = new Set<PresenceScope>(['tracker', 'billing-claims', 'billing-encounters']);

const SCOPE_TO_SUBJECT: Record<PresenceScope, PresenceSubjectResourceType> = {
  tracker: 'ServiceRequest',
  'billing-claims': 'Claim',
  'billing-encounters': 'Encounter',
};

const SUBJECT_PREFIXES: Record<PresenceSubjectResourceType, string> = {
  ServiceRequest: 'ServiceRequest/',
  Claim: 'Claim/',
  Encounter: 'Encounter/',
};

/** Composite lookup key for `presenceByRow`. */
export function presenceRowKey(scope: PresenceScope, rowId: string): string {
  return `${scope}:${rowId}`;
}

/** Up-to-two-letter initials from a display name; falls back to "?". */
export function getInitials(name: string | undefined): string {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/** Broad WebSocket subscription / seed criteria — matches every active presence resource. */
export function presenceSubscriptionCriteria(): string {
  return `Basic?code=${encodeURIComponent(PRESENCE_CODE_SYSTEM)}|${PRESENCE_CODE}`;
}

/** Query string (no resourceType) for `medplum.searchResources('Basic', ...)`. */
export function presenceSearchQuery(): string {
  return `code=${encodeURIComponent(PRESENCE_CODE_SYSTEM)}|${PRESENCE_CODE}`;
}

/** Stable unique value identifying one browser tab's presence resource. */
export function sessionIdentifierValue(practitionerId: string, tabId: string): string {
  return `${practitionerId}:${tabId}`;
}

/** Conditional-upsert query targeting this tab's single presence resource. */
export function presenceSessionIdentifierQuery(practitionerId: string, tabId: string): string {
  const value = sessionIdentifierValue(practitionerId, tabId);
  return `identifier=${encodeURIComponent(PRESENCE_SESSION_IDENTIFIER_SYSTEM)}|${encodeURIComponent(value)}`;
}

export interface BuildPresenceBasicArgs {
  practitioner: Practitioner;
  tabId: string;
  rowId: string;
  scope: PresenceScope;
  subjectResourceType: PresenceSubjectResourceType;
  name: string;
  photoUrl?: string;
  active: boolean;
  /** Milliseconds until this presence is considered stale (becomes the `expiresAt`). */
  ttlMs: number;
}

/** Build the PHI-free presence `Basic` for the current user/tab/row. */
export function buildPresenceBasic({
  practitioner,
  tabId,
  rowId,
  scope,
  subjectResourceType,
  name,
  photoUrl,
  active,
  ttlMs,
}: BuildPresenceBasicArgs): Basic {
  const payload: PresenceDataPayload = {
    scope,
    rowId,
    name,
    initials: getInitials(name),
    ...(photoUrl ? { photoUrl } : {}),
    expiresAt: new Date(Date.now() + ttlMs).toISOString(),
    active,
  };
  return {
    resourceType: 'Basic',
    code: { coding: [{ system: PRESENCE_CODE_SYSTEM, code: PRESENCE_CODE }] },
    identifier: [
      {
        system: PRESENCE_SESSION_IDENTIFIER_SYSTEM,
        value: sessionIdentifierValue(practitioner.id ?? '', tabId),
      },
    ],
    author: { reference: `Practitioner/${practitioner.id ?? ''}` },
    subject: { reference: `${subjectResourceType}/${rowId}` },
    extension: [{ url: PRESENCE_DATA_EXT, valueString: JSON.stringify(payload) }],
  };
}

function idFromReference(reference: string | undefined, prefix: string): string | undefined {
  if (!reference?.startsWith(prefix)) return undefined;
  const id = reference.slice(prefix.length);
  return id || undefined;
}

function subjectTypeFromReference(
  reference: string | undefined,
): PresenceSubjectResourceType | undefined {
  if (!reference) return undefined;
  for (const [type, prefix] of Object.entries(SUBJECT_PREFIXES) as [
    PresenceSubjectResourceType,
    string,
  ][]) {
    if (reference.startsWith(prefix)) return type;
  }
  return undefined;
}

function resolveScope(data: PresenceDataPayload): PresenceScope | null {
  const scope = data.scope ?? 'tracker';
  return KNOWN_SCOPES.has(scope) ? scope : null;
}

/** Dedupe sessions into actors keyed by presenceRowKey; excludes the current user. */
export function recomputePresenceByRow(
  sessions: Iterable<PresenceSession>,
  myActorId: string | undefined,
  now = Date.now(),
): Map<string, PresenceActor[]> {
  const byRow = new Map<string, Map<string, PresenceActor>>();
  for (const session of sessions) {
    if (session.expiresAtMs <= now) continue;
    if (session.actorId === myActorId) continue;
    const key = presenceRowKey(session.scope, session.rowId);
    let actors = byRow.get(key);
    if (!actors) {
      actors = new Map();
      byRow.set(key, actors);
    }
    if (!actors.has(session.actorId)) {
      actors.set(session.actorId, {
        actorId: session.actorId,
        name: session.name,
        initials: session.initials,
        ...(session.photoUrl ? { photoUrl: session.photoUrl } : {}),
      });
    }
  }
  const result = new Map<string, PresenceActor[]>();
  for (const [key, actors] of byRow) result.set(key, [...actors.values()]);
  return result;
}

export function presenceTargetsEqual(
  a: PresenceTarget | null,
  b: PresenceTarget | null,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    a.rowId === b.rowId &&
    a.scope === b.scope &&
    a.subjectResourceType === b.subjectResourceType
  );
}

/** Parse a presence `Basic` into a session, or null if it isn't a valid presence resource. */
export function parsePresence(basic: Basic): PresenceSession | null {
  const isPresence = basic.code?.coding?.some(
    (c) => c.system === PRESENCE_CODE_SYSTEM && c.code === PRESENCE_CODE,
  );
  if (!isPresence) return null;

  const raw = basic.extension?.find((e) => e.url === PRESENCE_DATA_EXT)?.valueString;
  if (!raw) return null;

  let data: PresenceDataPayload;
  try {
    data = JSON.parse(raw) as PresenceDataPayload;
  } catch {
    return null;
  }

  const scope = resolveScope(data);
  if (!scope) return null;

  const subjectResourceType = subjectTypeFromReference(basic.subject?.reference);
  if (!subjectResourceType) return null;
  if (SCOPE_TO_SUBJECT[scope] !== subjectResourceType) return null;

  const actorId = idFromReference(basic.author?.reference, 'Practitioner/');
  const sessionId = basic.identifier?.find(
    (i) => i.system === PRESENCE_SESSION_IDENTIFIER_SYSTEM,
  )?.value;
  const rowId =
    data.rowId ?? idFromReference(basic.subject?.reference, SUBJECT_PREFIXES[subjectResourceType]);
  if (!actorId || !sessionId || !rowId) return null;

  const expiresAtMs = Date.parse(data.expiresAt);

  return {
    actorId,
    sessionId,
    scope,
    rowId,
    subjectResourceType,
    name: data.name ?? '',
    initials: data.initials || getInitials(data.name),
    ...(data.photoUrl ? { photoUrl: data.photoUrl } : {}),
    expiresAtMs: Number.isNaN(expiresAtMs) ? 0 : expiresAtMs,
    active: data.active !== false,
  };
}
